import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants, companies, areas } from './common';
import { workers } from './workers';

export const revokedPrintedQrTokens = pgTable("revoked_printed_qr_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  revokedToken: text("revoked_token").notNull(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }).defaultNow(),
  revokedByUserId: text("revoked_by_user_id"),
  reason: text("reason"),
});
export const insertRevokedPrintedQrTokenSchema = createInsertSchema(revokedPrintedQrTokens).omit({ id: true, revokedAt: true });
export type InsertRevokedPrintedQrToken = z.infer<typeof insertRevokedPrintedQrTokenSchema>;
export type RevokedPrintedQrToken = typeof revokedPrintedQrTokens.$inferSelect;
export const accessRequests = pgTable("access_requests", {
  id: varchar("id").primaryKey(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerName: text("worker_name").notNull(),
  companyId: text("company_id").references(() => companies.id),
  role: text("role").notNull(),
  areaId: text("area_id").references(() => areas.id),
  requestedDate: text("requested_date"),
  status: text("status").notNull().default("pending"),
  slaDeadline: text("sla_deadline"),
  evidence: text("evidence").array().default([]),
  edited: boolean("edited").notNull().default(false),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  decidedAt: timestamp("decided_at", { withTimezone: true }),
}, (t) => ({
  idxAccessRequestsTenantStatus: index("idx_access_requests_tenant_status").on(t.tenantId, t.status),
  idxAccessRequestsTenantDecidedAt: index("idx_access_requests_tenant_decided_at").on(t.tenantId, t.decidedAt),
}));
export const insertAccessRequestSchema = createInsertSchema(accessRequests).omit({ tenantId: true });
export type InsertAccessRequest = z.infer<typeof insertAccessRequestSchema>;
export type AccessRequest = typeof accessRequests.$inferSelect;

// ─── Safety & Incident Management ─────────────────────────────────────
export const approvalWorkflows = pgTable("approval_workflows", {
  id: varchar("id").primaryKey(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  policyId: text("policy_id").notNull(),
  moduleType: text("module_type").notNull(),
  areaId: text("area_id"),
  riskLevelCondition: text("risk_level_condition").notNull().default("Any"),
  stages: jsonb("stages").$type<{
    stageOrder: number;
    roleRequired: string;
    mandatory: boolean;
    parallelApproval: boolean;
    timeoutHours: number;
    escalateToRole?: string;
    approvalCondition: string;
  }[]>().default([]),
  escalationEnabled: boolean("escalation_enabled").notNull().default(false),
  autoApprovalAllowed: boolean("auto_approval_allowed").notNull().default(false),
  rejectionStopsWorkflow: boolean("rejection_stops_workflow").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertApprovalWorkflowSchema = createInsertSchema(approvalWorkflows).omit({ tenantId: true });
export type InsertApprovalWorkflow = z.infer<typeof insertApprovalWorkflowSchema>;
export type ApprovalWorkflow = typeof approvalWorkflows.$inferSelect;
export const accessPolicies = pgTable("access_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  scopeType: text("scope_type").notNull().default("site"),
  scopeId: text("scope_id"),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  companyId: text("company_id"),
  priority: integer("priority").notNull().default(100),
  evaluationMode: text("evaluation_mode").notNull().default("allow"),
  effectiveFrom: text("effective_from"),
  effectiveTo: text("effective_to"),
  status: text("status").notNull().default("draft"),
  currentVersion: integer("current_version").notNull().default(1),
  minimumParticipationRoleCode: text("minimum_participation_role_code"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertAccessPolicySchema = createInsertSchema(accessPolicies).omit({ id: true, tenantId: true });
export type InsertAccessPolicy = z.infer<typeof insertAccessPolicySchema>;
export type AccessPolicy = typeof accessPolicies.$inferSelect;

// ─── Access Policy Rules ─────────────────────────────────────────────
export const accessPolicyRules = pgTable("access_policy_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  policyId: text("policy_id").notNull(),
  ruleName: text("rule_name").notNull(),
  dimension: text("dimension").notNull(),
  operator: text("operator").notNull().default("equals"),
  expectedValue: text("expected_value"),
  expectedValues: text("expected_values").array().default([]),
  failureMessage: text("failure_message"),
  overridable: boolean("overridable").notNull().default(true),
  ruleOrder: integer("rule_order").notNull().default(1),
  enabled: boolean("enabled").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertAccessPolicyRuleSchema = createInsertSchema(accessPolicyRules).omit({ id: true, tenantId: true });
export type InsertAccessPolicyRule = z.infer<typeof insertAccessPolicyRuleSchema>;
export type AccessPolicyRule = typeof accessPolicyRules.$inferSelect;

// ─── Access Policy Versions ──────────────────────────────────────────
export const accessPolicyVersions = pgTable("access_policy_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  policyId: text("policy_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  snapshotRules: jsonb("snapshot_rules").$type<Record<string, any>[]>().default([]),
  snapshotPolicy: jsonb("snapshot_policy").$type<Record<string, any>>().default({}),
  publishedBy: text("published_by"),
  publishedAt: text("published_at"),
  immutable: boolean("immutable").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertAccessPolicyVersionSchema = createInsertSchema(accessPolicyVersions).omit({ id: true, tenantId: true });
export type InsertAccessPolicyVersion = z.infer<typeof insertAccessPolicyVersionSchema>;
export type AccessPolicyVersion = typeof accessPolicyVersions.$inferSelect;

// ─── Access Events ───────────────────────────────────────────────────
export const accessEvents = pgTable("access_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull().default("entry"),
  subjectType: text("subject_type").notNull().default("worker"),
  subjectId: text("subject_id").notNull(),
  subjectName: text("subject_name"),
  companyId: text("company_id"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  direction: text("direction"),
  timestamp: text("timestamp").notNull(),
  decisionResult: text("decision_result").notNull().default("allow"),
  denialReasons: jsonb("denial_reasons").$type<string[]>().default([]),
  evaluatedPolicies: jsonb("evaluated_policies").$type<{ policyId: string; versionId?: string; result: string }[]>().default([]),
  unmetRequirements: jsonb("unmet_requirements").$type<string[]>().default([]),
  overrideId: text("override_id"),
  verificationMode: text("verification_mode"),
  deviceId: text("device_id"),
  deviceMetadata: jsonb("device_metadata").$type<Record<string, any>>().default({}),
  offlineFlag: boolean("offline_flag").notNull().default(false),
  ptwPermitId: text("ptw_permit_id"),
  occupancyDelta: integer("occupancy_delta"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxAccessEventsTenantCreatedAt: index("idx_access_events_tenant_created_at").on(t.tenantId, t.createdAt),
  idxAccessEventsTenantSubject: index("idx_access_events_tenant_subject").on(t.tenantId, t.subjectId),
}));
export const insertAccessEventSchema = createInsertSchema(accessEvents).omit({ id: true, tenantId: true });
export type InsertAccessEvent = z.infer<typeof insertAccessEventSchema>;
export type AccessEvent = typeof accessEvents.$inferSelect;

// ─── Access Overrides ────────────────────────────────────────────────
export const accessOverrides = pgTable("access_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  accessEventId: text("access_event_id"),
  subjectId: text("subject_id").notNull(),
  subjectType: text("subject_type").notNull().default("worker"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  bypassedPolicyId: text("bypassed_policy_id"),
  bypassedPolicyVersionId: text("bypassed_policy_version_id"),
  unmetConditions: jsonb("unmet_conditions").$type<string[]>().default([]),
  overrideReason: text("override_reason").notNull(),
  reasonNote: text("reason_note"),
  approvedBy: text("approved_by").notNull(),
  approvedByName: text("approved_by_name"),
  approvedByRole: text("approved_by_role"),
  approvalTimestamp: text("approval_timestamp").notNull(),
  duration: text("duration"),
  expiresAt: text("expires_at"),
  evidenceRefs: text("evidence_refs").array().default([]),
  status: text("status").notNull().default("active"),
  guardWorkerId: text("guard_worker_id"),
  gateName: text("gate_name"),
  zone: text("zone"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertAccessOverrideSchema = createInsertSchema(accessOverrides).omit({ id: true, tenantId: true });
export type InsertAccessOverride = z.infer<typeof insertAccessOverrideSchema>;
export type AccessOverride = typeof accessOverrides.$inferSelect;

// ─── Emergency Controls ──────────────────────────────────────────────
export const emergencyControls = pgTable("emergency_controls", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  controlType: text("control_type").notNull().default("lockdown"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  status: text("status").notNull().default("active"),
  reason: text("reason").notNull(),
  activatedBy: text("activated_by").notNull(),
  activatedByName: text("activated_by_name"),
  activatedAt: text("activated_at").notNull(),
  deactivatedBy: text("deactivated_by"),
  deactivatedByName: text("deactivated_by_name"),
  deactivatedAt: text("deactivated_at"),
  linkedIncidentId: text("linked_incident_id"),
  allowedRoles: text("allowed_roles").array().default([]),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertEmergencyControlSchema = createInsertSchema(emergencyControls).omit({ id: true, tenantId: true });
export type InsertEmergencyControl = z.infer<typeof insertEmergencyControlSchema>;
export type EmergencyControl = typeof emergencyControls.$inferSelect;

// ─── Worker Lifecycle & Deployment ───────────────────────────────────
export const tenantSigningKeys = pgTable("tenant_signing_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  keyId: text("key_id").notNull().unique(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  publicKey: text("public_key").notNull(),
  privateKeyEncrypted: text("private_key_encrypted"),
  keyType: text("key_type").notNull().default("EdDSA"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  // ── Parallel/legacy columns retained for QR signing engine raw SQL ─────
  // server/engines/qr-signing-engine.ts writes algorithm/status/
  // public_key_encoded at INSERT time (mirroring keyType/publicKey for the
  // verifier service's older field names). Keep them in lockstep until the
  // signing engine is migrated to the canonical columns.
  //
  // SECURITY: `private_key_encoded` is DEPRECATED and must NEVER receive
  // plaintext key material. The signing engine writes NULL into this
  // column; only `private_key_encrypted` (above, fernet-wrapped) is the
  // authoritative private-material field. The column remains nullable so
  // legacy verifier reads do not crash, and is scheduled for DROP once
  // every read path has been migrated.
  algorithm: text("algorithm"),
  status: text("status"),
  publicKeyEncoded: text("public_key_encoded"),
  privateKeyEncoded: text("private_key_encoded"),
  rotatedAt: timestamp("rotated_at", { withTimezone: true }),
  createdBy: text("created_by"),
});
export const insertTenantSigningKeySchema = createInsertSchema(tenantSigningKeys).omit({ id: true, createdAt: true });
export type InsertTenantSigningKey = z.infer<typeof insertTenantSigningKeySchema>;
export type TenantSigningKey = typeof tenantSigningKeys.$inferSelect;

// ─── QR Audit Log ─────────────────────────────────────────────────────
export const qrAuditLogs = pgTable("qr_audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  qrToken: text("qr_token").notNull(),
  workerId: text("worker_id").notNull(),
  scannedById: text("scanned_by_id"),
  scanContext: text("scan_context").notNull().default("verification"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  result: text("result").notNull(),
  failureReason: text("failure_reason"),
  crossTenantAttempt: boolean("cross_tenant_attempt").notNull().default(false),
  requestingTenantId: text("requesting_tenant_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxQrAuditLogsTenantWorkerId: index("idx_qr_audit_logs_tenant_worker_id").on(t.tenantId, t.workerId),
  idxQrAuditLogsTenantCreatedAt: index("idx_qr_audit_logs_tenant_created_at").on(t.tenantId, t.createdAt),
}));
export const insertQrAuditLogSchema = createInsertSchema(qrAuditLogs).omit({ id: true, createdAt: true });
export type InsertQrAuditLog = z.infer<typeof insertQrAuditLogSchema>;
export type QrAuditLog = typeof qrAuditLogs.$inferSelect;

// ─── QR Cross-Tenant Verifier Grants ──────────────────────────────────
// Super Admin grants allow verifiers from one tenant to verify QR codes issued by another tenant
export const qrCrossTenantGrants = pgTable("qr_cross_tenant_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  grantedById: text("granted_by_id").notNull(),
  grantedToTenantId: text("granted_to_tenant_id").notNull(),
  reason: text("reason"),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revokedById: text("revoked_by_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertQrCrossTenantGrantSchema = createInsertSchema(qrCrossTenantGrants).omit({ id: true, createdAt: true });
export type InsertQrCrossTenantGrant = z.infer<typeof insertQrCrossTenantGrantSchema>;
export type QrCrossTenantGrant = typeof qrCrossTenantGrants.$inferSelect;

// ─── Anomaly Rules (Real-time Access Checks) ─────────────────────────
export const anomalyRules = pgTable("anomaly_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  ruleType: text("rule_type").notNull(),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  enabled: boolean("enabled").notNull().default(true),
  platformLocked: boolean("platform_locked").notNull().default(false),
  config: jsonb("config").$type<Record<string, any>>().default({}),
  action: text("action").notNull().default("flag"),
  severity: text("severity").notNull().default("medium"),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertAnomalyRuleSchema = createInsertSchema(anomalyRules).omit({ id: true, tenantId: true, createdAt: true, updatedAt: true });
export type InsertAnomalyRule = z.infer<typeof insertAnomalyRuleSchema>;
export type AnomalyRule = typeof anomalyRules.$inferSelect;

// ─── Anomaly Events ───────────────────────────────────────────────────
export const anomalyEvents = pgTable("anomaly_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  ruleId: text("rule_id").notNull(),
  ruleType: text("rule_type").notNull(),
  subjectId: text("subject_id").notNull(),
  subjectName: text("subject_name"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),
  accessEventId: text("access_event_id"),
  severity: text("severity").notNull().default("medium"),
  status: text("status").notNull().default("open"),
  flaggedAt: text("flagged_at").notNull(),
  reviewedBy: text("reviewed_by"),
  reviewedByRole: text("reviewed_by_role"),
  reviewedAt: text("reviewed_at"),
  reviewNotes: text("review_notes"),
  overrideGranted: boolean("override_granted").notNull().default(false),
  details: jsonb("details").$type<Record<string, any>>().default({}),
  actorId: text("actor_id"),
  actorRole: text("actor_role"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Pack 6 / 6.A.1 — schema-forward index: drives anomaly-sla-engine open-event scan.
  byStatusFlaggedAt: index("anomaly_events_status_flagged_at_idx").on(t.status, t.flaggedAt),
}));
export const insertAnomalyEventSchema = createInsertSchema(anomalyEvents).omit({ id: true, tenantId: true, createdAt: true });
export type InsertAnomalyEvent = z.infer<typeof insertAnomalyEventSchema>;
export type AnomalyEvent = typeof anomalyEvents.$inferSelect;

// ─── Gate Metrics (Queue Monitoring) ─────────────────────────────────
export const gateMetrics = pgTable("gate_metrics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  gateId: text("gate_id").notNull(),
  siteId: text("site_id"),
  windowStart: text("window_start").notNull(),
  windowEnd: text("window_end").notNull(),
  queueDepth: integer("queue_depth").notNull().default(0),
  throughput: integer("throughput").notNull().default(0),
  avgWaitSeconds: integer("avg_wait_seconds").notNull().default(0),
  allowedCount: integer("allowed_count").notNull().default(0),
  deniedCount: integer("denied_count").notNull().default(0),
  overrideCount: integer("override_count").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertGateMetricSchema = createInsertSchema(gateMetrics).omit({ id: true, tenantId: true, createdAt: true });
export type InsertGateMetric = z.infer<typeof insertGateMetricSchema>;
export type GateMetric = typeof gateMetrics.$inferSelect;

// ─── Offline Decision Cache ───────────────────────────────────────────
export const offlineDecisionCache = pgTable("offline_decision_cache", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  gateId: text("gate_id").notNull(),
  siteId: text("site_id"),
  subjectId: text("subject_id").notNull(),
  subjectType: text("subject_type").notNull().default("worker"),
  cachedDecision: text("cached_decision").notNull().default("deny"),
  credentialSnapshot: jsonb("credential_snapshot").$type<Record<string, any>>().default({}),
  cachedAt: text("cached_at").notNull(),
  expiresAt: text("expires_at").notNull(),
  usedOffline: boolean("used_offline").notNull().default(false),
  offlineUsedAt: text("offline_used_at"),
  reconciledAt: text("reconciled_at"),
  accessEventId: text("access_event_id"),
  auditReviewed: boolean("audit_reviewed").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertOfflineDecisionCacheSchema = createInsertSchema(offlineDecisionCache).omit({ id: true, tenantId: true, createdAt: true });
export type InsertOfflineDecisionCache = z.infer<typeof insertOfflineDecisionCacheSchema>;
export type OfflineDecisionCache = typeof offlineDecisionCache.$inferSelect;

// ─── Platform Access Governance (Super Admin) ─────────────────────────
export const platformAccessGovernance = pgTable("platform_access_governance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  value: jsonb("value").$type<Record<string, any>>().default({}),
  description: text("description").notNull().default(""),
  setBy: text("set_by").notNull(),
  setAt: timestamp("set_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type PlatformAccessGovernance = typeof platformAccessGovernance.$inferSelect;

// ─── Proactive Credential Alerts ──────────────────────────────────────
